import styled from 'styled-components';

import { Color, colors, fontWeights } from 'theme';

import { ButtonVariant } from './Button.types';
import {
  buttonAppearance,
  buttonSize,
  buttonStylesMinimal,
  linkAppearance,
} from './buttonStyles';

// name of colors for icon initial color
export const TEXT_COLORS: Record<ButtonVariant, Color> = {
  primary: 'white',
  secondary: 'waterBlue',
  'secondary-white': 'white',
  danger: 'white',
  'danger-outline': 'brick',
  'legacy-tertiary': 'greyishBrown',
  'legacy-tertiary-blue': 'waterBlue',
  'legacy-tertiary-white': 'white',
  tertiary: 'waterBlue',
  'tertiary-grey': 'greyishBrown',
  'tertiary-danger': 'brick',
  link: 'waterBlue',
  'link-white': 'white',
  inactive: 'greyishBrown',
};

export const StyledButton = styled.button`
  ${buttonStylesMinimal};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid transparent;

  &.full-width {
    width: 100%;
  }

  /* button variants: */
  &.primary {
    ${buttonAppearance}
    background-color: ${colors.waterBlue};
    border-color: ${colors.waterBlue};
    color: ${colors[TEXT_COLORS.primary]};
    &:hover,
    &:focus {
      background-color: ${colors.hoverBlue};
      border-color: ${colors.hoverBlue};
      text-decoration: none;
    }
  }
  &.secondary {
    ${buttonAppearance}
    border-color: ${colors.waterBlue};
    color: ${colors[TEXT_COLORS.secondary]};
    &:hover,
    &:focus {
      background-color: ${colors.softBlue};
      text-decoration: none;
    }
  }
  &.secondary-white {
    ${buttonAppearance}
    border-color: ${colors.white};
    color: ${TEXT_COLORS['secondary-white']};
    &:hover,
    &:focus {
      color: ${colors.waterBlue};
      background-color: ${colors.softBlue};
      text-decoration: none;
    }
  }
  &.danger {
    ${buttonAppearance}
    background-color: ${colors.scarlet};
    border-color: ${colors.scarlet};
    color: ${colors[TEXT_COLORS.danger]};
    &:hover,
    &:focus {
      background-color: ${colors.brick};
      border-color: ${colors.brick};
      text-decoration: none;
    }
  }
  &.danger-outline {
    ${buttonAppearance}
    border-color: ${colors.scarlet};
    color: ${colors[TEXT_COLORS['danger-outline']]};
    &:hover,
    &:focus {
      background-color: ${colors.brick};
      color: white;
      text-decoration: none;
    }
  }
  &.legacy-tertiary {
    ${buttonAppearance}
    color: ${colors[TEXT_COLORS['legacy-tertiary']]};

    &:hover,
    &:focus {
      border-color: ${colors.greyishBrown};
      text-decoration: none;
    }
  }
  &.legacy-tertiary-blue {
    ${buttonAppearance}
    color: ${TEXT_COLORS['legacy-tertiary-blue']};

    &:hover,
    &:focus {
      text-decoration: none;
    }
  }
  &.legacy-tertiary-white {
    ${buttonAppearance}
    color: ${TEXT_COLORS['legacy-tertiary-white']};

    &:hover,
    &:focus {
      text-decoration: none;
    }
  }
  &.tertiary {
    ${buttonAppearance}
    color: ${colors[TEXT_COLORS.tertiary]};
    font-weight: ${fontWeights.bold};
    &:hover,
    &:focus {
      color: ${colors.hoverBlue};
      text-decoration: underline;
    }
  }
  &.tertiary-grey {
    ${buttonAppearance}
    color: ${colors[TEXT_COLORS['tertiary-grey']]};
    font-weight: ${fontWeights.bold};
    &:hover,
    &:focus {
      text-decoration: underline;
    }
  }
  &.tertiary-danger {
    ${buttonAppearance}
    color: ${colors[TEXT_COLORS['tertiary-danger']]};
    font-weight: ${fontWeights.bold};
    &:hover,
    &:focus {
      text-decoration: underline;
    }
  }
  &.link {
    /* block-no-empty */
    ${linkAppearance}
  }
  &.link-white {
    ${linkAppearance}
    color: white;
    text-decoration: underline;

    &:hover {
      color: white;
    }
  }
  &.inactive {
    ${buttonAppearance}
    background-color: ${colors.aluminum};
    color: ${colors[TEXT_COLORS.inactive]};
  }
  /* button sizes: */
  &.tiny {
    /* block-no-empty */
    ${buttonSize({ size: 'tiny' })};
  }
  &.small {
    /* block-no-empty */
    ${buttonSize({ size: 'small' })};
  }
  &.medium {
    /* block-no-empty */
    ${buttonSize({ size: 'medium' })};
  }
  &.large {
    /* block-no-empty */
    ${buttonSize({ size: 'large' })};
  }
  &.small,
  &.medium,
  &.large {
    &.no-padding {
      padding: 0;
    }
  }
`;

export const ChildrenContainer = styled.div`
  text-align: center;
  width: 100%;
`;

// icons have a negative margin to compensate for padding internal to the SVG
export const IconContainer = styled.div`
  margin: -1px -2px;
`;
